/**
 * workspaceAuthority.ts
 * Canonical Single Source of Truth for all Wingman Workspaces.
 *
 * Provides authoritative definitions, metadata, spoken aliases,
 * and unified execution for pulling up any active workspace:
 * - Flight Showroom (Travel)
 * - Real Estate Intelligence (Travel)
 * - Flight Concierge (Travel)
 * - Write Studio (Create)
 * - Wingman Workspace (Create)
 * - Voice Studio (Create)
 * - Cockpit (Primary 3D Spatial Cockpit)
 * - Intel Portal & DoD Workspace (Intelligence)
 */

import { workspaceRegistry } from './WorkspaceRegistry';
import { contextState } from '../context/contextState';

export type WorkspaceId =
  | 'flight-showroom'
  | 'real-estate-intelligence'
  | 'flight-concierge'
  | 'write-studio'
  | 'wingman-workspace'
  | 'ai-expert-studio'
  | 'voice-studio'
  | 'radio'
  | 'cockpit'
  | 'intel-portal'
  | 'dod-workspace';

export interface WorkspaceDefinition {
  readonly id: WorkspaceId;
  readonly title: string;
  readonly category: 'travel' | 'create' | 'intelligence' | 'cockpit';
  readonly description: string;
  readonly badge?: string;
  readonly status: 'ACTIVE' | 'LOCKED_COMING_SOON';
  readonly spokenAliases: readonly string[];
  readonly open: (params?: any) => { success: boolean; message: string };
  readonly close?: () => void;
}

export const WORKSPACE_CATALOG: readonly WorkspaceDefinition[] = [
  // ── TRAVEL GROUP ──────────────────────────────────────────────────────────
  {
    id: 'flight-showroom',
    title: 'Flight Showroom',
    category: 'travel',
    description: '3D Globe & curated worldwide deals',
    status: 'ACTIVE',
    spokenAliases: [
      'flight showroom',
      'showroom',
      'flight deals',
      'open flight showroom',
      'pull up flight showroom',
      'show flight deals',
      'explore flight deals',
      'travel showroom',
      'flights'
    ],
    open: () => {
      contextState.setPanelMode('none');
      const ok = workspaceRegistry.openWorkspace('flight-showroom');
      return {
        success: ok,
        message: 'Opened Flight Showroom with 3D Globe and curated worldwide flight deals.'
      };
    },
    close: () => {
      workspaceRegistry.closeWorkspace();
    }
  },
  {
    id: 'real-estate-intelligence',
    title: 'Real Estate Intelligence',
    category: 'travel',
    description: 'Global property command center',
    status: 'ACTIVE',
    spokenAliases: [
      'real estate intelligence',
      'real estate showroom',
      'real estate show room',
      'property showroom',
      'realty showroom',
      'real estate',
      'property command center',
      'pull up real estate',
      'open real estate',
      'real estate workspace',
      'property search',
      'properties',
      'homes'
    ],
    open: () => {
      contextState.setPanelMode('none');
      const ok = workspaceRegistry.openWorkspace('real-estate-intelligence');
      return {
        success: ok,
        message: 'Opened Real Estate Intelligence global property command center.'
      };
    },
    close: () => {
      workspaceRegistry.closeWorkspace();
    }
  },
  {
    id: 'flight-concierge',
    title: 'Flight Concierge',
    category: 'travel',
    description: 'Interactive flight route assistant',
    status: 'ACTIVE',
    spokenAliases: [
      'flight concierge',
      'concierge',
      'flight route assistant',
      'route assistant',
      'pull up flight concierge',
      'open flight concierge',
      'flight assistant'
    ],
    open: () => {
      workspaceRegistry.closeWorkspace();
      contextState.setPanelMode('flightConcierge');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('WINGMAN_WORKSPACE_CHANGED', { detail: { activeId: 'flight-concierge' } }));
      }
      return {
        success: true,
        message: 'Opened Flight Concierge interactive flight route assistant.'
      };
    },
    close: () => {
      contextState.setPanelMode('none');
    }
  },

  // ── CREATE GROUP ──────────────────────────────────────────────────────────
  {
    id: 'write-studio',
    title: 'Write Studio',
    category: 'create',
    description: 'Executive document & report composition',
    status: 'ACTIVE',
    spokenAliases: [
      'write studio',
      'writing studio',
      'writer',
      'pull up write studio',
      'open write studio',
      'document studio',
      'report composition',
      'executive writing'
    ],
    open: (params?: any) => {
      workspaceRegistry.closeWorkspace();
      contextState.setPanelMode('none');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('OPEN_WRITING_STUDIO', {
            detail: {
              title: params?.title || 'Wingman Executive Writing Workspace',
              content: params?.content || '# Wingman Executive Intelligence Workspace\n\nWelcome to your dedicated Writing Studio. Draft, edit, format, and export executive briefs.',
              format: params?.format || 'Executive Workspace',
              tone: params?.tone || 'Executive'
            }
          })
        );
      }
      return {
        success: true,
        message: 'Opened Write Studio for executive document and report composition.'
      };
    },
    close: () => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('CLOSE_WRITING_STUDIO'));
      }
    }
  },
  {
    id: 'wingman-workspace',
    title: 'Wingman Workspace',
    category: 'create',
    description: 'Spatial intelligence executive workspace',
    status: 'ACTIVE',
    spokenAliases: [
      'wingman workspace',
      'executive workspace',
      'open wingman workspace',
      'pull up wingman workspace',
      'spatial workspace',
      'open workspace'
    ],
    open: () => {
      contextState.setPanelMode('none');
      const ok = workspaceRegistry.openWorkspace('wingman-workspace');
      return {
        success: ok,
        message: 'Opened Wingman Workspace spatial intelligence executive command center.'
      };
    },
    close: () => {
      workspaceRegistry.closeWorkspace();
    }
  },
  {
    id: 'ai-expert-studio',
    title: 'AI Expert Studio',
    category: 'create',
    description: 'Autonomous AI expert advisory suite',
    badge: 'NEW',
    status: 'ACTIVE',
    spokenAliases: [
      'ai expert studio',
      'expert studio',
      'expert suite',
      'open ai expert studio',
      'open expert studio',
      'pull up expert studio',
      'ai experts',
      'experts'
    ],
    open: () => {
      contextState.setPanelMode('none');
      const ok = workspaceRegistry.openWorkspace('ai-expert-studio');
      return {
        success: ok,
        message: 'Opened AI Expert Studio autonomous advisory workspace.'
      };
    },
    close: () => {
      workspaceRegistry.closeWorkspace();
    }
  },
  {
    id: 'voice-studio',
    title: 'Voice Studio',
    category: 'create',
    description: 'Natural conversational voice tuning',
    status: 'ACTIVE',
    spokenAliases: [
      'voice studio',
      'voice tuning',
      'pull up voice studio',
      'open voice studio',
      'voice settings',
      'natural conversational voice tuning',
      'tune voice'
    ],
    open: () => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('OPEN_VOICE_STUDIO'));
      }
      return {
        success: true,
        message: 'Opened Voice Studio for natural conversational voice tuning.'
      };
    },
    close: () => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('CLOSE_VOICE_STUDIO'));
      }
    }
  },
  {
    id: 'radio',
    title: 'Wingman Radio',
    category: 'create',
    description: 'Live global internet radio & ambient broadcast station',
    badge: 'LIVE',
    status: 'ACTIVE',
    spokenAliases: [
      'radio',
      'wingman radio',
      'live radio',
      'internet radio',
      'open radio',
      'pull up radio',
      'pull up the radio',
      'pull up the radio station',
      'pull up radio station',
      'open radio station',
      'open the radio station',
      'radio station',
      'radio stations',
      'radio showroom',
      'start radio',
      'play radio',
      'play the radio',
      'stations'
    ],
    open: () => {
      contextState.setPanelMode('none');
      const ok = workspaceRegistry.openWorkspace('radio');
      return {
        success: ok,
        message: 'Opened Wingman Radio live global broadcast directory.'
      };
    },
    close: () => {
      workspaceRegistry.closeWorkspace();
    }
  },

  // ── COCKPIT (PRIMARY 3D SPATIAL MAP) ──────────────────────────────────────
  {
    id: 'cockpit',
    title: 'Cockpit',
    category: 'cockpit',
    description: 'Primary 3D spatial map & cockpit',
    status: 'ACTIVE',
    spokenAliases: [
      'cockpit',
      'map',
      '3d map',
      'back to cockpit',
      'back to map',
      'return to map',
      'return to cockpit',
      'exit workspace',
      'close workspace',
      'close showroom',
      'pull up cockpit'
    ],
    open: () => {
      workspaceRegistry.closeWorkspace();
      contextState.setPanelMode('none');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('CLOSE_WRITING_STUDIO'));
        window.dispatchEvent(new CustomEvent('CLOSE_VOICE_STUDIO'));
        window.dispatchEvent(new CustomEvent('WINGMAN_WORKSPACE_CHANGED', { detail: { activeId: null } }));
      }
      return {
        success: true,
        message: 'Returned to primary 3D Cockpit Map.'
      };
    },
    close: () => {}
  },

  // ── INTELLIGENCE GROUP ────────────────────────────────────────────────────
  {
    id: 'intel-portal',
    title: 'Intel Portal',
    category: 'intelligence',
    description: 'Quick reference & intelligence shortcuts',
    status: 'ACTIVE',
    spokenAliases: [
      'intel portal',
      'intelligence portal',
      'open intel portal',
      'pull up intel portal',
      'cheatsheet',
      'shortcuts'
    ],
    open: () => {
      if (typeof window !== 'undefined' && (window as any).__openCheatsheet) {
        (window as any).__openCheatsheet();
      }
      return {
        success: true,
        message: 'Opened Intel Portal quick reference.'
      };
    }
  },
  {
    id: 'dod-workspace',
    title: 'DoD Workspace',
    category: 'intelligence',
    description: 'Strategic & defense intelligence operations',
    badge: 'COMING SOON',
    status: 'LOCKED_COMING_SOON',
    spokenAliases: [
      'dod workspace',
      'defense workspace',
      'military workspace',
      'pull up dod workspace'
    ],
    open: () => {
      return {
        success: false,
        message: 'DoD Workspace is locked pending security certification (Coming Soon).'
      };
    }
  }
];

export class WorkspaceAuthorityService {
  private static instance: WorkspaceAuthorityService | null = null;
  private catalog = new Map<WorkspaceId, WorkspaceDefinition>();

  private constructor() {
    WORKSPACE_CATALOG.forEach(spec => this.catalog.set(spec.id, spec));
  }

  public static getInstance(): WorkspaceAuthorityService {
    if (!WorkspaceAuthorityService.instance) {
      WorkspaceAuthorityService.instance = new WorkspaceAuthorityService();
    }
    return WorkspaceAuthorityService.instance;
  }

  public getAll(): readonly WorkspaceDefinition[] {
    return Array.from(this.catalog.values());
  }

  public get(id: WorkspaceId): WorkspaceDefinition | undefined {
    return this.catalog.get(id);
  }

  public getActiveWorkspaceId(): string | null {
    const regActive = workspaceRegistry.getActiveWorkspaceId();
    if (regActive) return regActive;
    if (contextState.getPanelMode() === 'flightConcierge') return 'flight-concierge';
    return 'cockpit';
  }

  /**
   * Pulls up a workspace by canonical ID or resolves from natural query / alias.
   */
  public pullUpWorkspace(
    target: string,
    params?: any
  ): { success: boolean; workspaceId: WorkspaceId | 'unknown'; message: string } {
    if (!target || typeof target !== 'string') {
      return {
        success: false,
        workspaceId: 'unknown',
        message: 'Invalid workspace identifier requested.'
      };
    }

    const resolved = this.resolveWorkspace(target);
    if (!resolved) {
      return {
        success: false,
        workspaceId: 'unknown',
        message: `Workspace "${target}" is not recognized.`
      };
    }

    if (resolved.status === 'LOCKED_COMING_SOON') {
      return {
        success: false,
        workspaceId: resolved.id,
        message: `${resolved.title} is currently coming soon and locked.`
      };
    }

    const result = resolved.open(params);
    return {
      success: result.success,
      workspaceId: resolved.id,
      message: result.message
    };
  }

  /**
   * Resolves a spoken user phrase or identifier to a canonical WorkspaceDefinition.
   */
  public resolveWorkspace(query: string): WorkspaceDefinition | undefined {
    const normalized = query.trim().toLowerCase();

    // 1. Direct ID match
    if (this.catalog.has(normalized as WorkspaceId)) {
      return this.catalog.get(normalized as WorkspaceId);
    }

    // 2. Exact Title match
    for (const spec of this.catalog.values()) {
      if (spec.title.toLowerCase() === normalized) {
        return spec;
      }
    }

    // 3. Spoken Alias exact matches first across all workspaces
    for (const spec of this.catalog.values()) {
      for (const alias of spec.spokenAliases) {
        if (normalized === alias) {
          return spec;
        }
      }
    }

    // 4. Flatten all aliases with their spec, sorted by length descending (longest/most specific match wins)
    const allAliases: Array<{ alias: string; spec: WorkspaceDefinition }> = [];
    for (const spec of this.catalog.values()) {
      for (const alias of spec.spokenAliases) {
        allAliases.push({ alias, spec });
      }
    }
    allAliases.sort((a, b) => b.alias.length - a.alias.length);

    for (const entry of allAliases) {
      if (normalized.includes(entry.alias)) {
        return entry.spec;
      }
    }

    // 5. Keyword heuristics
    if (normalized.includes('real estate') || normalized.includes('property') || normalized.includes('properties')) {
      return this.catalog.get('real-estate-intelligence');
    }
    if (normalized.includes('concierge') && normalized.includes('flight')) {
      return this.catalog.get('flight-concierge');
    }
    if (normalized.includes('showroom') || normalized.includes('flight deal') || normalized.includes('flights')) {
      return this.catalog.get('flight-showroom');
    }
    if (normalized.includes('write') || normalized.includes('writing') || normalized.includes('document')) {
      return this.catalog.get('write-studio');
    }
    if (normalized.includes('voice studio') || normalized.includes('voice tuning') || normalized.includes('voice setting')) {
      return this.catalog.get('voice-studio');
    }
    if (normalized.includes('wingman workspace') || (normalized.includes('executive') && normalized.includes('workspace'))) {
      return this.catalog.get('wingman-workspace');
    }
    if (normalized.includes('cockpit') || (normalized.includes('back') && normalized.includes('map')) || normalized.includes('return to map')) {
      return this.catalog.get('cockpit');
    }

    return undefined;
  }
}

export const workspaceAuthority = WorkspaceAuthorityService.getInstance();
export const pullUpWorkspace = (target: string, params?: any) => workspaceAuthority.pullUpWorkspace(target, params);

if (typeof window !== 'undefined') {
  (window as any).workspaceAuthority = workspaceAuthority;
  (window as any).pullUpWorkspace = pullUpWorkspace;
}
